import type { GenderPreset } from '../../theme';

/** Standard family roles offered on the "Village" sharing step. */
export type VillageRole = 'partner' | 'grandparent' | 'sibling' | 'extended' | 'carer' | 'friend';

/**
 * How the parent tagged a previewed milestone: they've seen the baby do it
 * ('seen' → in progress) or it's already a done deal ('achieved'). Mirrors the
 * Today screen's "I saw this!" interaction.
 */
export type MilestoneMark = 'seen' | 'achieved';

/**
 * Everything the multi-step wizard collects before the auth gateway. Lives in
 * OnboardingFlow local state; the baby subset (name/dob/gender) is bubbled up to
 * the App-level Bootstrap so it survives the unauth → auth flip and gets POSTed.
 */
export interface OnboardingData {
  /** Baby's name — captured on the personalisation step. */
  name: string;
  /** Date of birth, YYYY-MM-DD. */
  dob: string;
  /** Theme accent / gender. */
  gender: GenderPreset;
  /** Carers the parent intends to invite (default: none selected). */
  village: VillageRole[];
  /** Milestones the parent tagged on the preview step, keyed by milestone id. */
  milestoneMarks: Record<number, MilestoneMark>;
}

export const emptyOnboarding: OnboardingData = {
  name: '',
  dob: '',
  gender: 'neutral',
  village: [],
  milestoneMarks: {},
};

export const ONBOARDING_STORAGE_KEY = 'babysteps.onboarding.draft';

export function loadStoredOnboarding(): { step: number; data: OnboardingData } | null {
  try {
    const raw = sessionStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearOnboardingDraft(): void {
  try {
    sessionStorage.removeItem(ONBOARDING_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Whole weeks since birth (matches derive.rowToBaby: floor(days / 7)). */
export function ageWeeksFromDob(dob: string): number {
  if (!isValidDob(dob)) return 0;
  const days = (Date.now() - new Date(dob + 'T00:00:00').getTime()) / MS_PER_DAY;
  return Math.max(0, Math.floor(days / 7));
}

/** Age in months, rounded to one decimal (matches derive.rowToBaby). */
export function ageMonthsFromDob(dob: string): number {
  if (!isValidDob(dob)) return 0;
  const days = (Date.now() - new Date(dob + 'T00:00:00').getTime()) / MS_PER_DAY;
  return Math.max(0, Math.round((days / 30.4375) * 10) / 10);
}

/** A human label like "4 months" / "6 weeks" for headers. */
export function ageLabel(dob: string): string {
  const weeks = ageWeeksFromDob(dob);
  if (weeks < 8) return `${weeks} week${weeks === 1 ? '' : 's'}`;
  const months = Math.round(ageMonthsFromDob(dob));
  return `${months} month${months === 1 ? '' : 's'}`;
}

/** Whole-number months for the "[X] months old" copy. */
export function ageMonthsRounded(dob: string): number {
  return Math.max(0, Math.round(ageMonthsFromDob(dob)));
}

export function isValidDob(dob: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob)) return false;
  const d = new Date(dob + 'T00:00:00');
  return d.toString() !== 'Invalid Date' && d.getTime() <= Date.now();
}

/** "14 Feb 2026" style label for review screens. */
export function formatDob(dob: string): string {
  if (!isValidDob(dob)) return '—';
  return new Date(dob + 'T00:00:00').toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export const VILLAGE_OPTIONS: { role: VillageRole; label: string; sub: string }[] = [
  { role: 'partner', label: 'Partner / Co-Parent', sub: 'Shares everything with you' },
  { role: 'grandparent', label: 'Grandparent', sub: 'Loves the photo updates' },
  { role: 'sibling', label: 'Sibling', sub: 'A proud big brother or sister' },
  { role: 'extended', label: 'Extended Family', sub: 'Aunts, uncles & cousins' },
  { role: 'carer', label: 'Carer / Nanny', sub: 'Logs the day-to-day' },
  { role: 'friend', label: 'Friend', sub: 'Cheers the milestones on' },
];
